import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

function formatPhone(val) {
  if (!val) return val;
  const d = String(val).replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0,3)})${d.slice(3)}`;
  return `(${d.slice(0,3)})${d.slice(3,6)}-${d.slice(6)}`;
}

const SIZES = ['XS*','S','M','L','XL','2XL','3XL'];
const PRICE_PER_EXTRA = 8;
const TABS = ['Personal Info','Forms & Uploads','Downloads','Availability','Optional','Confirm & Submit'];

const FANCY_ROLES = ['Venue Setup/Teardown','Decorations Setup/Teardown','Silent Auction','Games','Bartender Assistant','Registration','Photographer','Kitchen Support','Staff Child Care'];

const empty = {
  personal: { firstName:'', lastName:'', dob:'', phone:'', email:'', address:'', city:'', state:'', zip:'', emergencyName:'', emergencyRelationship:'', emergencyPhone:'', allergies:'', medications:'', medicalNotes:'', ssn:'', bankName:'', routing:'', accountNumber:'', shirts: Object.fromEntries(SIZES.map(s=>[s,0])) },
  forms: { w4:null, i9:null, i9_listA:null, i9_listB:null, i9_listC:null, medical:null, directdeposit:null, cpr:null, lifeguard:null, bgCheckDone:false },
  availability: { ropes:null, ropesLevel:'', fancy:null, fancyRoles:[], junior:null, juniorDates:'' },
  optional: { muggers:null, musicInterest:null, musicDatesUnavailable:'', musicInstruments:'', venmoNote:'' },
  checks: { accuracy:false, policies:false, texts:false, liability:false },
};

const CURRENT_YEAR = new Date().getFullYear();

function UserMenuWithPhoto({ email, photoUrl, onSignOut, onBack }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const item = (icon, label, onClick, danger) => (
    <button
      onClick={() => { setOpen(false); onClick(); }}
      style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 16px', fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: danger ? '#c0392b' : '#2D2D2D', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 9 }}
      onMouseEnter={e => e.currentTarget.style.background = '#F7FAF2'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      <span style={{ fontSize: '1rem', width: 20, textAlign: 'center' }}>{icon}</span>
      {label}
    </button>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: photoUrl ? '3px 8px 3px 3px' : '6px 12px', fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.8rem', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
      >
        {photoUrl
          ? <img src={photoUrl} alt="Profile" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.6)' }} />
          : <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</span>
        }
        <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>▾</span>
      </button>

      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', background: '#fff', borderRadius: 12, boxShadow: '0 6px 24px rgba(0,0,0,0.14)', minWidth: 210, zIndex: 2000, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px 8px', fontSize: '0.75rem', color: '#9B9B9B', borderBottom: '1px solid #F0F7E8', fontWeight: 600 }}>{email}</div>
          {onBack && item('👤', 'My Profile', onBack)}
          <div style={{ borderTop: '1px solid #F0F7E8', marginTop: 4 }}>
            {item('🚪', 'Sign out', onSignOut, true)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OnboardingForm({ session, onSignOut, onBack }) {
  const [tab, setTab]               = useState(0);
  const [data, setData]             = useState(empty);
  const [uploading, setUploading]   = useState({});
  const [uploadNames, setUploadNames] = useState({});
  const [saving, setSaving]         = useState(false);
  const [lastSaved, setLastSaved]   = useState(null);
  const [submitted, setSubmitted]   = useState(false);
  const [loadedRow, setLoadedRow]   = useState(null);
  const [docsViewed, setDocsViewed] = useState(false);
  const saveTimer = useRef(null);


  // Load existing submission on mount
  useEffect(() => {
    const load = async () => {
      const { data: rows } = await supabase
        .from('onboarding_submissions')
        .select('*')
        .eq('email', session.user.email)
        .single();
      if (rows) {
        setLoadedRow(rows);
        // If they have an existing submission, they've already visited Downloads
        setDocsViewed(true);
        // Don't auto-redirect to success screen on load — only show it after
        // the user explicitly clicks Submit in this session
        setData({
          personal:     { ...empty.personal,     ...(rows.personal_info  || {}), shirts: { ...empty.personal.shirts, ...(rows.personal_info?.shirts || {}) } },
          forms:        { ...empty.forms,         ...(rows.forms          || {}) },
          availability: { ...empty.availability,  ...(rows.availability   || {}) },
          optional:     { ...empty.optional,      ...(rows.optional_info  || {}) },
          checks:       { ...empty.checks,        ...(rows.checks         || {}) },
        });
        if (rows.forms) {
          const names = {};
          ['w4','i9','i9_listA','i9_listB','i9_listC','medical','directdeposit','cpr','lifeguard'].forEach(k => {
            if (rows.forms[k]) {
              const parts = rows.forms[k].split('/').pop().split('_');
              const cleanName = parts.slice(2).join('_') || rows.forms[k].split('/').pop();
              names[k] = cleanName;
            }
          });
          setUploadNames(names);
        }
      }
    };
    load();
  }, [session]);

  // Auto-save whenever data changes (debounced 1.5s)
  const save = useCallback(async (d) => {
    setSaving(true);
    const payload = {
      email: session.user.email,
      user_id: session.user.id,
      personal_info: d.personal,
      forms: d.forms,
      availability: d.availability,
      optional_info: d.optional,
      checks: d.checks,
      updated_at: new Date().toISOString(),
    };
    await supabase.from('onboarding_submissions').upsert(payload, { onConflict: 'email' });
    setSaving(false);
    setLastSaved(new Date());
  }, [session]);

  const update = (section, field, value) => {
    setData(prev => {
      const next = { ...prev, [section]: { ...prev[section], [field]: value } };
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => save(next), 1500);
      return next;
    });
  };

  const uploadFile = async (key, file) => {
    if (!file) return;
    setUploading(u => ({ ...u, [key]: true }));
    // Sanitize filename — replace spaces with underscores
    const safeName = file.name.replace(/\s+/g, '_');
    const path = `${session.user.id}/${key}_${Date.now()}_${safeName}`;
    const { error } = await supabase.storage.from('onboarding-files').upload(path, file, { upsert: true });
    if (!error) {
      setUploadNames(n => ({ ...n, [key]: safeName }));
      update('forms', key, path);
    } else {
      console.error('Upload error:', error.message);
    }
    setUploading(u => ({ ...u, [key]: false }));
  };
  const removeFile = (key) => {
    setUploadNames(n => ({ ...n, [key]: null }));
    update('forms', key, null);
  };
  const handleSubmit = async () => {
    setSaving(true);
    // Only set submitted_at on first submission; preserve it on re-saves
    const existingSubmittedAt = loadedRow?.submitted_at || new Date().toISOString();
    await supabase.from('onboarding_submissions').upsert({
      email: session.user.email,
      user_id: session.user.id,
      personal_info: data.personal,
      forms: data.forms,
      availability: data.availability,
      optional_info: data.optional,
      checks: data.checks,
      submitted_at: existingSubmittedAt,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'email' });
    setSaving(false);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Tab completion logic
  const isTabComplete = (i) => {
    const p = data.personal; const f = data.forms; const a = data.availability; const c = data.checks;
    if (i === 0) return !!(p.firstName && p.lastName && p.phone && p.email && p.address && p.city && p.state && p.zip && p.emergencyName && p.emergencyPhone && p.emergencyRelationship && Object.values(p.shirts).reduce((a,b)=>a+b,0) > 0);
    if (i === 1) return !!(f.w4 && f.i9 && f.medical && f.cpr && f.directdeposit);
    if (i === 2) return docsViewed;
    if (i === 3) return a.ropes !== null && a.fancy !== null && a.junior !== null && (a.ropes !== true || a.ropesLevel) && (a.ropesLevel !== 'Other' || a.ropesOtherDetail);
    if (i === 4) return true; // optional always complete
    if (i === 5) return !!(c.accuracy && c.policies && c.texts && c.liability);
    return false;
  };

  if (submitted) return <SuccessScreen onEdit={() => { setSubmitted(false); setTab(0); window.scrollTo({top:0}); }} onGoToProfile={onBack} />;

  const shirtTotal = SIZES.reduce((a, size) => a + (data.personal.shirts[size] || 0), 0);
  const extraCost  = Math.max(0, shirtTotal - 1) * PRICE_PER_EXTRA;

  const completedTabs = TABS.filter((_, i) => isTabComplete(i)).length;

  return (
    <div style={{ ...s.root, backgroundImage: 'url(/camp_bg.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Responsive styles */}
      <style>{`
        .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .doc-card-actions { display: flex; gap: 8px; }
        @media (max-width: 600px) {
          .row2 { grid-template-columns: 1fr; }
          .form-header { flex-direction: column; align-items: flex-start !important; }
          .form-header > div:last-child { align-items: flex-start !important; }
          .form-card { padding: 18px !important; }
          .doc-card-actions { flex-direction: column; }
        }
      `}</style>
      {/* Header */}
      <div style={s.header} className="form-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="/Camp_Phillip_Logo.png" alt="Camp Phillip" style={{ height: 56, filter: 'brightness(0) invert(1)' }} />
          <div>
            <div style={s.badge}>SALT Onboarding</div>
            <p style={s.sub}>Welcome! Your progress saves automatically.</p>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span role="status" aria-live="polite" style={{ fontSize: '0.75rem', color: saving ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.5)', minHeight: '1.1em' }}>
            {saving ? 'Saving…' : lastSaved ? '✓ All changes saved' : ''}
          </span>
          <UserMenuWithPhoto
            email={session.user.email}
            photoUrl={loadedRow?.directory_profile?.photoUrl}
            onSignOut={onSignOut}
            onBack={onBack}
          />
        </div>
      </div>

      {/* Tab bar */}
      <div style={s.tabWrap}>
        <div style={s.tabRow}>
          {TABS.map((t, i) => {
           const complete = isTabComplete(i); return (
           <button key={t} style={{ ...s.tabBtn, ...(tab === i ? s.tabActive : complete ? s.tabDone : {}) }}
              onClick={() => { if (tab === 2) setDocsViewed(true); setTab(i); }}>
             {t}{complete && tab !== i ? ' ✓' : ''}
               </button>
               );
             })}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '16px 16px 0' }}>
        <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 12, padding: '14px 18px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', border: '1px solid #C8D9B0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2D5016' }}>{completedTabs} of {TABS.length} sections complete</span>
            <span style={{ fontSize: '0.78rem', color: '#6B7F5A' }}>{Math.round((completedTabs / TABS.length) * 100)}%</span>
          </div>
          <div style={{ height: 8, background: '#E8F0DE', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(completedTabs / TABS.length) * 100}%`, background: 'linear-gradient(90deg, #2D5016, #7BA05B)', borderRadius: 4, transition: 'width 0.4s ease' }} />
          </div>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.card} className="form-card">

          {/* ── TAB 0: PERSONAL INFO ── */}
          {tab === 0 && <>
            <SectionTitle title="Personal Information" sub="Please fill out all required fields. This information is used for Camp records only." />
            <SubHeading>Basic Info</SubHeading>
            <Row2>
              <Field label="First Name" req><input style={s.input} value={data.personal.firstName} onChange={e=>update('personal','firstName',e.target.value)} placeholder="Jane"/></Field>
              <Field label="Last Name" req><input style={s.input} value={data.personal.lastName} onChange={e=>update('personal','lastName',e.target.value)} placeholder="Doe"/></Field>
            </Row2>
            <Row2>
              <Field label="Date of Birth" req><input style={{ ...s.input, fontSize: '0.85rem' }} type="date" max={new Date().toISOString().split('T')[0]} value={data.personal.dob} onChange={e=>update('personal','dob',e.target.value)}/></Field>
              <Field label="Phone Number" req><input style={s.input} type="tel" maxLength={14} value={data.personal.phone} onChange={e => update('personal','phone', formatPhone(e.target.value))} placeholder="(414)555-0100"/></Field>
            </Row2>
            <Field label="Email Address" req><input style={s.input} type="email" value={data.personal.email} onChange={e=>update('personal','email',e.target.value)} placeholder="jane@email.com"/></Field>
            <Divider/>
            <SubHeading>Address</SubHeading>
            <Field label="Street Address" req><input style={s.input} value={data.personal.address} onChange={e=>update('personal','address',e.target.value)} placeholder="123 Main St"/></Field>
            <Row2>
              <Field label="City" req><input style={s.input} value={data.personal.city} onChange={e=>update('personal','city',e.target.value)} placeholder="Milwaukee"/></Field>
              <Field label="State" req>
                <select style={s.input} value={data.personal.state} onChange={e=>update('personal','state',e.target.value)}>
                  <option value="">Select…</option>
                  {['WI','IL','MN','MI','IA','Other'].map(st=><option key={st}>{st}</option>)}
                </select>
              </Field>
            </Row2>
            <Row2><Field label="ZIP Code" req><input style={s.input} inputMode="numeric" pattern="\d{5}(-\d{4})?" maxLength={10} value={data.personal.zip} onChange={e=>update('personal','zip',e.target.value.replace(/[^\d\-]/g, ''))} placeholder="53201"/></Field><div/></Row2>
            <Divider/>
            <SubHeading>Emergency Contact</SubHeading>
            <Row2>
              <Field label="Contact Name" req><input style={s.input} value={data.personal.emergencyName} onChange={e=>update('personal','emergencyName',e.target.value)} placeholder="John Doe"/></Field>
              <Field label="Relationship" req><input style={s.input} value={data.personal.emergencyRelationship} onChange={e=>update('personal','emergencyRelationship',e.target.value)} placeholder="Parent, Spouse…"/></Field>
            </Row2>
            <Field label="Contact Phone" req><input style={s.input} type="tel" maxLength={14} value={data.personal.emergencyPhone} onChange={e => update('personal','emergencyPhone', formatPhone(e.target.value))} placeholder="(414)555-0101"/></Field>
            <Divider/>
            <SubHeading>Medical Information</SubHeading>
            <Field label="Allergies" opt><textarea style={s.textarea} value={data.personal.allergies} onChange={e=>update('personal','allergies',e.target.value)} placeholder="List any allergies…"/></Field>
            <Field label="Medications" opt><textarea style={s.textarea} value={data.personal.medications} onChange={e=>update('personal','medications',e.target.value)} placeholder="List any medications you take…"/></Field>
            <Field label="Medical Conditions / Notes" opt><textarea style={s.textarea} value={data.personal.medicalNotes} onChange={e=>update('personal','medicalNotes',e.target.value)} placeholder="Anything the camp should be aware of…"/></Field>
            <Divider/>
            <SubHeading>🎽 Staff T-Shirt Order</SubHeading>
            <InfoBox variant="sun"><strong>Your first shirt is FREE!</strong> Each additional shirt is <strong>$8.00</strong>. You may order shirts in multiple sizes.</InfoBox>
            <div style={{ display: 'grid', gap: 10 }}>
              {SIZES.map(size => (
                <div key={size} style={s.shirtRow}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Size {size}</div>
                    {size === 'XS*' && (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                      <span
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#7BA05B', color: '#fff', fontSize: '0.65rem', fontWeight: 700, cursor: 'default' }}
                        onMouseEnter={e => { e.currentTarget.nextSibling.style.display = 'block'; }}
                        onMouseLeave={e => { e.currentTarget.nextSibling.style.display = 'none'; }}
                        >i</span>
                      <div style={{ display: 'none', position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)', background: '#2D5016', color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: '0.78rem', whiteSpace: 'nowrap', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', lineHeight: 1.4 }}>
                      If XS is not available, a Small will be ordered instead.
                      </div>
                      </div>
                      )}  
                  </div>
                    <div style={{ fontSize: '0.8rem', color: '#6B7F5A', minHeight: '1.2em' }}>{data.personal.shirts[size] > 0 ? `${data.personal.shirts[size]} shirt(s)` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button style={s.qtyBtn} onClick={() => update('personal','shirts',{...data.personal.shirts,[size]:Math.max(0,data.personal.shirts[size]-1)})}>−</button>
                    <span style={{ fontWeight: 700, width: 24, textAlign: 'center' }}>{data.personal.shirts[size]}</span>
                    <button style={s.qtyBtn} onClick={() => update('personal','shirts',{...data.personal.shirts,[size]:data.personal.shirts[size]+1})}>+</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ ...s.shirtTotal, flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <p style={{ fontSize: '0.82rem', color: '#6B7F5A', marginTop: 8, fontStyle: 'italic' }}>Payments for Staff Shirts will be collected at SALT Training.</p>
              <span>Additional Shirt Cost:</span>
              <span>${extraCost.toFixed(2)}</span>
            </div>
            <NavRow onNext={() => { setTab(1); window.scrollTo({top:0}); }} />
          </>}

          {/* ── TAB 1: FORMS & UPLOADS ── */}
          {tab === 1 && <>
            <SectionTitle title="Forms & Uploads" sub="Download each form, complete it, and upload your signed copy. Required forms must be submitted before your first day." />
            <SubHeading>Employment Forms</SubHeading>
            <Field label="W-4 & WT4 (Federal and State Withholding)" req>
              <InfoBox variant="sky" style={{ marginBottom: 10 }}><a href="/2026_Form_W-4.pdf" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={s.docLink}>📥 Download the 2026 W-4 Form</a> — fill it out, sign it, and upload your completed copy below.</InfoBox>
              <UploadField fieldKey="w4" label="Upload Completed W-4" icon="📄" uploading={uploading.w4} fileName={uploadNames.w4} onFile={f => uploadFile('w4', f)} onRemove={() => removeFile('w4')}/>
            </Field>
            <Field label="I-9 (Employment Eligibility)" req>
              <InfoBox variant="sky" style={{ marginBottom: 10 }}><a href="/Form_I-9_Employment_Eligibility_Verification.pdf" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={s.docLink}>📥 Download Form I-9</a> — fill it out, sign it, and upload your completed copy below.</InfoBox>
              <I9Helper />
              <UploadField fieldKey="i9" label="Upload Completed I-9 Form" icon="📄" uploading={uploading.i9} fileName={uploadNames.i9} onFile={f => uploadFile('i9', f)} onRemove={() => removeFile('i9')} />
                <div style={{ marginTop: 12 }}>
               <p style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2D5016', marginBottom: 8 }}>Supporting Documents <span style={{ fontWeight: 400, color: '#6B7F5A' }}>(optional — upload List A, or both List B and List C)</span></p>
               <UploadField fieldKey="i9_listA" label="List A Document (e.g. Passport)" icon="🪪" uploading={uploading.i9_listA} fileName={uploadNames.i9_listA} onFile={f => uploadFile('i9_listA', f)} onRemove={() => removeFile('i9_listA')}/>
                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                  <div style={{ flex: 1, height: 1, background: '#C8D9B0' }}/><span style={{ fontSize: '0.78rem', color: '#6B7F5A', fontWeight: 600 }}>or</span><div style={{ flex: 1, height: 1, background: '#C8D9B0' }}/>
                 </div>
                <UploadField fieldKey="i9_listB" label="List B Document (e.g. Driver's License)" icon="🪪" uploading={uploading.i9_listB} fileName={uploadNames.i9_listB} onFile={f => uploadFile('i9_listB', f)} onRemove={() => removeFile('i9_listB')} />
                <UploadField fieldKey="i9_listC" label="List C Document (e.g. Social Security Card)" icon="📄" uploading={uploading.i9_listC} fileName={uploadNames.i9_listC} onFile={f => uploadFile('i9_listC', f)} onRemove={() => removeFile('i9_listC')}/>
               </div>
              </Field>
            <Field label="Direct Deposit Authorization" req>
              <InfoBox variant="sky" style={{ marginBottom: 10 }}> <a href="/Direct_Deposit_Tax_Info.pdf" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={s.docLink}>📥 Download the Direct Deposit Form</a> — fill it out, sign it, and upload your completed copy below. Please attach a voided check or bank form.</InfoBox>
              <UploadField fieldKey="directdeposit" label="Upload Completed Direct Deposit Form" icon="🏦" uploading={uploading.directdeposit} fileName={uploadNames.directdeposit} onFile={f => uploadFile('directdeposit', f)} onRemove={() => removeFile('directdeposit')}/>
            </Field>
            <Divider/>
            <SubHeading>Medical Forms</SubHeading>
            <Field label="Adult Health Form" req>
              <InfoBox variant="sky" style={{ marginBottom: 10 }}><a href="/Adult_Health_Form.pdf" target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={s.docLink}>📥 Download the Adult Health Form</a> — fill it out, sign it, and upload your completed copy below.</InfoBox>
              <UploadField fieldKey="medical" label="Upload Completed Health Form" icon="🏥" uploading={uploading.medical} fileName={uploadNames.medical} onFile={f => uploadFile('medical', f)} onRemove={() => removeFile('medical')} />
            </Field>
            <Divider/>
            <SubHeading>Certifications</SubHeading>
            <Field label="CPR / First Aid Certificate" req>
              <UploadField fieldKey="cpr" label="Upload CPR / First Aid Cert" icon="🩺" uploading={uploading.cpr} fileName={uploadNames.cpr} onFile={f => uploadFile('cpr', f)} onRemove={() => removeFile('cpr')} />
            </Field>
            <Field label="Lifeguard Certificate" opt>
              <UploadField fieldKey="lifeguard" label="Upload Lifeguard Cert" icon="🏊" uploading={uploading.lifeguard} fileName={uploadNames.lifeguard} onFile={f => uploadFile('lifeguard', f)} onRemove={() => removeFile('lifeguard')} />
            </Field>
            <Divider/>
            <SubHeading>Background Check</SubHeading>
            <InfoBox variant="sun">⚠️ A background check is only required <strong>every other year</strong> for all staff (SALT, J-Staff, and volunteers). Background checks were performed for all staff in 2025. If you are unsure whether you need to complete one, please email <strong>Brett@campphillip.com</strong> before proceeding.</InfoBox>
            <Field label="Background Check (if required)" opt>
              <InfoBox variant="sky" style={{ marginBottom: 10 }}>Complete your background check at: <a href="https://campphillip.quickapp.pro/forms" target="_blank" rel="noreferrer" style={s.docLink}>campphillip.quickapp.pro/forms</a></InfoBox>
              <label style={s.checkLabel}>
                <input type="checkbox" checked={data.forms.bgCheckDone} onChange={e => update('forms','bgCheckDone',e.target.checked)} style={{ width: 18, height: 18, accentColor: '#2D5016', flexShrink: 0, marginTop: 2 }}/>
                <span>I have completed my background check via the link above, or I completed a background check in 2025.</span>
              </label>
            </Field>
            <NavRow onBack={() => { setTab(0); window.scrollTo({top:0}); }} onNext={() => { setTab(2); window.scrollTo({top:0}); }} />
          </>}

          {/* ── TAB 2: DOWNLOADS ── */}
          {tab === 2 && <>
            <SectionTitle title="Downloads" sub="These documents are for your reference. Please review each one before arrival at Camp." />
            {[
              { icon: '📘', title: 'SALT Manual', desc: 'Camp policies, expectations, and community guidelines', href: '/SALT_Manual_Update.pdf' },
              { icon: '📅', title: '2026 Program Calendar', desc: 'Session dates and key events', href: '/Camp_Program_Calendar_2026.jpg' },
              { icon: '🧳', title: 'Packing List', desc: 'What to bring and what to leave at home', href: '/SALT_Packing_List.pdf' },
              { icon: '📞', title: 'Year-Round Staff Contacts', desc: 'Key staff contacts and Camp phone numbers', href: '/Year_Round_Staff_Contacts.vcf' },
            ].map(doc => (
              <div key={doc.title} style={s.docCard}>
                <div style={{ fontSize: '1.5rem' }}>{doc.icon}</div>
                <div style={{ flex: 1 }}>
                  <strong style={{ display: 'block', fontSize: '0.92rem' }}>{doc.title}</strong>
                  <span style={{ fontSize: '0.8rem', color: '#6B7F5A' }}>{doc.desc}</span>
                </div>
                <a
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={s.docBtn}
                >
                  View / Download ↗
                </a>
              </div>
            ))}
            <Divider/>
              <div style={s.docCard}>
              <div style={{ fontSize: '1.5rem' }}>💬</div>
              <div style={{ flex: 1 }}>
              <strong style={{ display: 'block', fontSize: '0.92rem' }}>Slack</strong>
              <span style={{ fontSize: '0.8rem', color: '#6B7F5A' }}>Our primary communication tool for the summer — announcements, scheduling, and staff updates</span>
           </div>
           <div className="doc-card-actions">
            <a href="https://apps.apple.com/us/app/slack/id618783545" target="_blank" rel="noopener noreferrer" style={s.docBtn}>App Store ↗</a>
            <a href="https://play.google.com/store/apps/details?id=com.Slack" target="_blank" rel="noopener noreferrer" style={s.docBtn}>Google Play ↗</a>
           </div>
          </div>
            <NavRow onBack={() => { setTab(1); window.scrollTo({top:0}); }} onNext={() => { setDocsViewed(true); setTab(3); window.scrollTo({top:0}); }} />
          </>}

          {/* ── TAB 3: AVAILABILITY ── */}
          {tab === 3 && <>
            <SectionTitle title="Availability" />
            <InfoBox style={{ borderColor: '#2D5016', background: '#e6f0da' }}>📌 <strong>SALT Training</strong> begins the <strong>Sunday after Memorial Day</strong> and runs through the following Saturday. <strong>SALT Training is mandatory for all staff.</strong></InfoBox>
            <Divider/>
            <p style={{ fontSize: '0.92rem', color: '#6B7F5A', marginBottom: 20, fontStyle: 'italic' }}>Please indicate your availability for the optional events below.</p>
            <SubHeading>Ropes Training</SubHeading>
            <p style={{ fontSize: '0.85rem', color: '#6B7F5A', marginBottom: 14, fontStyle: 'italic' }}>Ropes Training begins on the evening of Memorial Day and goes through Friday evening.</p>
            <Field label="Will you attend Ropes Training?" req>
              <YNGroup value={data.availability.ropes} onChange={v => update('availability','ropes',v)} yesLabel="✅ Yes" noLabel="❌ No"/>
             {data.availability.ropes === true && (
  <Reveal>
    <Field label="In what capacity will you be attending?">
      {['Level 1 & 2 Trainee', 'Level 3 Trainee', 'Instructor', 'Other'].map(lv => (
        <label key={lv} style={s.radioOpt}>
          <input 
            type="radio" 
            name="ropes-level" 
            value={lv} 
            checked={data.availability.ropesLevel === lv} 
            onChange={() => update('availability', 'ropesLevel', lv)}
          />
          {lv}
        </label>
      ))}
    </Field>

    {/* Conditionally show the text input if "Other" is selected */}
    {data.availability.ropesLevel === 'Other' && (
      <Reveal>
        <Field label="Please specify your role:">
          <input 
            type="text" 
            placeholder="e.g. Photographer, Kitchen, etc."
            value={data.availability.ropesOtherDetail || ''} 
            onChange={e => update('availability', 'ropesOtherDetail', e.target.value)}
            style={s.input}
          />
        </Field>
      </Reveal>
    )}
  </Reveal>
)}
            </Field>
            <Divider/>
            <SubHeading>Fancy Schmancy Fundraiser</SubHeading>
            <InfoBox variant="sun">🍽️ The Fancy Schmancy Fundraiser takes place the <strong>Saturday before SALT Training begins</strong>. Volunteers will receive <strong>dinner at the event</strong>.</InfoBox>
            <Field label="Will you volunteer at the Fancy Schmancy Fundraiser?" req>
              <YNGroup value={data.availability.fancy} onChange={v => update('availability','fancy',v)} yesLabel="✅ Yes" noLabel="❌ No"/>
              {data.availability.fancy === true && (
                <Reveal>
                  <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#2D5016', marginBottom: 8 }}>Job Preferences <span style={{ fontWeight: 400, color: '#6B7F5A' }}>(select all that apply)</span></p>
                  <a href="https://docs.google.com/document/d/1N6TBqfDiHGqNrBphxDl5-XE2wz29Alb1izBJ6uaO0Mw/edit" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.82rem', color: '#2E7DAF', fontWeight: 700, display: 'inline-block', marginBottom: 12 }}>📄 View job descriptions ↗</a>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {FANCY_ROLES.map(role => {
                      const active = data.availability.fancyRoles.includes(role);
                      return (
                        <button key={role} style={{ ...s.chip, ...(active ? s.chipActive : {}) }}
                          onClick={() => update('availability','fancyRoles', active ? data.availability.fancyRoles.filter(r=>r!==role) : [...data.availability.fancyRoles,role])}>
                          {role}
                        </button>
                      );
                    })}
                  </div>
                </Reveal>
              )}
            </Field>
            <Divider/>
            <SubHeading>Junior Staff Training</SubHeading>
            <InfoBox>📌 <strong>Junior Staff Training</strong> runs <strong>Sunday through Friday</strong>, immediately following SALT Training.</InfoBox>
            <Field label="Will you be available for the full duration of Junior Staff Training?" req>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[['yes','✅ Yes, full duration'],['partial','⚠️ No, partial availability'],['no','❌ No availability']].map(([val,label]) => (
                  <button key={val} style={{ ...s.ynBtn, flex: 'none', ...(data.availability.junior === val ? (val==='yes' ? s.ynYes : val==='no' ? s.ynNo : s.ynPartial) : {}) }}
                    onClick={() => update('availability','junior',val)}>{label}</button>
                ))}
              </div>
              {data.availability.junior === 'partial' && (
                <Reveal><Field label="Which dates will you be unavailable?"><textarea style={s.textarea} value={data.availability.juniorDates} onChange={e => update('availability','juniorDates',e.target.value)} placeholder="e.g., I can't arrive until Monday evening, or I need to leave Thursday…"/></Field></Reveal>
              )}
            </Field>
            <NavRow onBack={() => { setTab(2); window.scrollTo({top:0}); }} onNext={() => { setTab(4); window.scrollTo({top:0}); }} />
          </>}

          {/* ── TAB 4: OPTIONAL ── */}
          {tab === 4 && <>
            <SectionTitle title="Optional" sub="None of these items are required, but they're a great way to get plugged in and make the most of your summer at Camp Phillip!" />
            <SubHeading>💸 Venmo</SubHeading>
            <InfoBox>Throughout the summer there are various things — like staff shirts, the SALT Retreat, and other camp activities — that involve payments. <strong>Venmo</strong> is a convenient and widely-used way to handle these quickly. We strongly encourage all staff to have the app ready to go before arrival.<br/><br/>
              <a href="https://apps.apple.com/us/app/venmo/id351727428" target="_blank" rel="noreferrer" style={s.docLink}>📱 Download Venmo on the App Store</a>&nbsp;&nbsp;|&nbsp;&nbsp;
              <a href="https://play.google.com/store/apps/details?id=com.venmo" target="_blank" rel="noreferrer" style={s.docLink}>📱 Download Venmo on Google Play</a>
            </InfoBox>
            <Divider/>
            <SubHeading>☕ Muggers</SubHeading>
            <InfoBox variant="sun"><strong>Muggers</strong> is for the coffee lovers. This is an optional club for SALT to join — it costs <strong>$25</strong> to join. All coffee drinkers are <em>highly encouraged</em> to sign up so they don't have to be subjected to Dining Hall coffee. ☕</InfoBox>
            <Field label="Would you like to join Muggers?">
              <YNGroup value={data.optional.muggers === 'yes' ? true : data.optional.muggers === 'no' ? false : null}
                onChange={v => update('optional','muggers', v ? 'yes' : 'no')}
                yesLabel="☕ Yes, I'm in!" noLabel="No thanks"/>
              {data.optional.muggers === 'yes' && <Reveal><InfoBox style={{ margin: 0 }}>🎉 Welcome to Muggers! Please <strong>Venmo $25</strong> to <strong>@esmidthun (Eli)</strong> to complete your membership. Put "Muggers" in the memo so he knows what it's for!</InfoBox></Reveal>}
            </Field>
            <Divider/>
            <SubHeading>🎵 Music for Church</SubHeading>
            <InfoBox>Eli organizes the worship schedule for the summer. If you're interested in playing preservice music, let him know your availability and how you'd like to contribute!</InfoBox>
            <Field label="Are you interested in playing music for church services?">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[['yes','🎸 Yes, I\'m interested in playing music!'],['no','No thanks']].map(([val,label]) => (
                  <label key={val} style={s.radioOpt}>
                    <input type="radio" name="music-interest" value={val} checked={data.optional.musicInterest === val} onChange={() => update('optional','musicInterest',val)}/>
                    {label}
                  </label>
                ))}
              </div>
              {data.optional.musicInterest === 'yes' && (
                <Reveal>
                  <Field label="Sundays Unavailable"><textarea style={s.textarea} value={data.optional.musicDatesUnavailable} onChange={e => update('optional','musicDatesUnavailable',e.target.value)} placeholder="List any dates you'd be unavailable to play…"/></Field>
                  <Field label="Instrument(s) / Vocals / Notes"><textarea style={s.textarea} value={data.optional.musicInstruments} onChange={e => update('optional','musicInstruments',e.target.value)} placeholder="e.g., acoustic guitar, vocals, piano, cajon — whatever you'd like Eli to know!"/></Field>
                </Reveal>
              )}
            </Field>
            <NavRow onBack={() => { setTab(3); window.scrollTo({top:0}); }} onNext={() => { setTab(5); window.scrollTo({top:0}); }} />
          </>}

          {/* ── TAB 5: CONFIRM & SUBMIT ── */}
          {tab === 5 && <>
            <SectionTitle title="Confirm & Submit" sub="Please review the agreements below and submit your onboarding package." />
            <InfoBox>✅ By submitting this form, you confirm that all information provided is accurate and complete to the best of your knowledge.</InfoBox>
            {[
              ['accuracy', 'I confirm that all information I have provided in this onboarding form is accurate and complete.'],
              ['policies', "I have read and agree to abide by Camp Phillip's policies and staff expectations as outlined in the SALT Manual."],
              ['texts',    "I agree to receive texts from Camp Phillip's Emergency Response System."],
              ['liability', 'I have read and understand the Camp Phillip Liability Waiver. I voluntarily assume all risks associated with camp activities, authorize Camp Phillip to seek emergency medical treatment on my behalf if needed, and release Camp Phillip, its employees, and volunteers from liability for any injury, damage, or loss sustained during my participation. I also give permission to be photographed for Camp Phillip\'s promotional use.'],
            ].map(([key, label]) => (
              <label key={key} style={s.checkLabel}>
                <input type="checkbox" checked={data.checks[key]} onChange={e => update('checks',key,e.target.checked)} style={{ width: 18, height: 18, accentColor: '#2D5016', flexShrink: 0, marginTop: 2 }}/>
                <span>{label}</span>
              </label>
            ))}
            <div style={{ ...s.navRow, marginTop: 28, flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
               <button style={s.backBtn} onClick={() => { setTab(4); window.scrollTo({top:0}); }}>← Back</button>
              <button style={s.submitBtn} disabled={saving || !Object.values(data.checks).every(Boolean) || !TABS.every((_tab, i) => isTabComplete(i))}
              onClick={handleSubmit}>
              {saving ? 'Submitting…' : '🏕️ Submit Onboarding'}
                </button>
              </div>
             {!TABS.every((_tab, i) => isTabComplete(i)) && (
              <p style={{ fontSize: '0.82rem', color: '#C0392B', marginTop: 10, textAlign: 'right' }}>
             Please complete all tabs before submitting. Incomplete tabs are shown without a ✓.
              </p>
             )}
            </div>
          </>}

        </div>
      </div>
    </div>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────
function I9Helper() {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ background: '#FEF6E4', borderLeft: '4px solid #F5A623', borderRadius: 8, padding: '12px 16px', marginBottom: 10, fontSize: '0.9rem', color: '#7a4e00' }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Nunito', sans-serif", fontSize: '0.9rem', fontWeight: 700, color: '#7a4e00', padding: 0, display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left' }}>
        <span>{open ? '▼' : '▶'}</span> First time filling out an I-9? Click here for document guidance.
      </button>
      {open && (
        <div style={{ marginTop: 12, lineHeight: 1.6 }}>
          You'll need to prove your identity and eligibility to work in the US. You can do this one of two ways:<br/><br/>
          <strong>Option A — One document from List A:</strong><br/>
          US Passport or Passport Card &nbsp;·&nbsp; Permanent Resident Card &nbsp;·&nbsp; Employment Authorization Document (Form I-766)<br/><br/>
          <strong>Option B — One document from List B + one from List C:</strong><br/>
          <em>List B (Identity):</em> Driver's license or state ID with photo &nbsp;·&nbsp; School ID with photo &nbsp;·&nbsp; Military ID<br/>
          <em>List C (Work Authorization):</em> Social Security card &nbsp;·&nbsp; Birth certificate &nbsp;·&nbsp; US Citizen ID Card<br/><br/>
          Most staff use their <strong>driver's license + Social Security card</strong>. You do not need to upload these — just bring them on your first day and a camp administrator will verify them in person.
        </div>
      )}

    </div>
  );
}
function SectionTitle({ title, sub }) {
  return (
    <>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', fontWeight: 700, color: '#2D5016', marginBottom: 6 }}>{title}</h2>
      {sub && <p style={{ fontSize: '0.9rem', color: '#6B7F5A', marginBottom: 24, lineHeight: 1.5 }}>{sub}</p>}
    </>
  );
}
function SubHeading({ children }) {
  return <div style={{ fontSize: '1rem', fontWeight: 700, color: '#3E6B1F', marginBottom: 14, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>{children}<div style={{ flex: 1, height: 2, background: '#C8D9B0', borderRadius: 2 }}/></div>;
}
function Divider() { return <hr style={{ border: 'none', borderTop: '2px dashed #C8D9B0', margin: '28px 0' }}/>; }
function Row2({ children }) { return <div className="row2">{children}</div>; }
function Field({ label, req, opt, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'block', fontWeight: 700, fontSize: '0.88rem', color: '#2D5016', marginBottom: 6 }}>
        {label}{req && <span style={{ color: '#C0392B', marginLeft: 2 }}>*</span>}
        {opt && <span style={{ color: '#6B7F5A', fontWeight: 400, fontSize: '0.82rem', marginLeft: 4 }}>(optional)</span>}
      </div>
      {children}
    </div>
  );
}
function InfoBox({ children, variant, style: extra }) {
  const bg = variant === 'sky' ? '#E8F4FC' : variant === 'sun' ? '#FEF6E4' : '#EAF4E2';
  const border = variant === 'sky' ? '#4A90C4' : variant === 'sun' ? '#F5A623' : '#7BA05B';
  const color = variant === 'sky' ? '#1a4a6b' : variant === 'sun' ? '#7a4e00' : '#2D5016';
  return <div style={{ background: bg, borderLeft: `4px solid ${border}`, borderRadius: 8, padding: '14px 16px', fontSize: '0.9rem', color, marginBottom: 20, lineHeight: 1.5, ...extra }}>{children}</div>;
}
function Reveal({ children }) { return <div style={{ marginTop: 14, padding: 14, background: '#F0F7E8', borderRadius: 10, border: '1px solid #C8D9B0' }}>{children}</div>; }
function YNGroup({ value, onChange, yesLabel = '✅ Yes', noLabel = '❌ No' }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: value !== null ? 0 : 0 }}>
      {[[true, yesLabel],[false, noLabel]].map(([val, label]) => (
        <button key={String(val)} style={{ ...s.ynBtn, flex: 1, ...(value === val ? (val ? s.ynYes : s.ynNo) : {}) }}
          onClick={() => onChange(val)}>{label}</button>
      ))}
    </div>
  );
}
function UploadField({ fieldKey, label, icon, uploading, fileName, onFile, onRemove }) {
  const id = `file-input-${fieldKey}`;
  return (
    <div style={{ marginBottom: 4 }}>
      <label
        htmlFor={id}
        onClick={e => e.stopPropagation()}
        style={{ ...s.uploadField, ...(uploading ? { opacity: 0.7, cursor: 'not-allowed' } : { cursor: 'pointer' }) }}
      >
        <span style={{ fontSize: '1.4rem' }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <strong style={{ display: 'block', fontSize: '0.92rem', color: '#2D5016' }}>
            {uploading ? 'Uploading…' : label}
          </strong>
          <span style={{ fontSize: '0.8rem', color: '#6B7F5A' }}>
            {fileName ? 'Click to replace file' : 'PDF, JPG, or PNG — click to browse'}
          </span>
        </div>
        {fileName
          ? <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#27AE60', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>✓ {fileName}</span>
          : <span style={{ fontSize: '0.8rem', color: '#aaa', whiteSpace: 'nowrap' }}>No file yet</span>
        }
      </label>
      {fileName && onRemove && (
        <button
          onClick={e => { e.stopPropagation(); onRemove(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C0392B', fontSize: '0.78rem', fontWeight: 700, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
          ✕ Remove file
        </button>
      )}
      <input
        id={id}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        disabled={uploading}
        style={{ display: 'none' }}
        onClick={e => e.stopPropagation()}
        onChange={e => {
          e.stopPropagation();
          const file = e.target.files[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
function NavRow({ onBack, onNext }) {
  return (
    <div style={s.navRow}>
      {onBack ? <button style={s.backBtn} onClick={onBack}>← Back</button> : <div/>}
      {onNext && <button style={s.nextBtn} onClick={onNext}>Next →</button>}
    </div>
  );
}
function SuccessScreen({ onEdit, onGoToProfile }) {
  return (
    <div style={{ fontFamily: "'Nunito', sans-serif", background: '#FDF8F0', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '48px 36px', maxWidth: 480, width: '100%', textAlign: 'center', boxShadow: '0 2px 16px rgba(45,80,22,0.08)', border: '1px solid #C8D9B0' }}>
        <div style={{ fontSize: '4rem', marginBottom: 16 }}>🎉</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", color: '#2D5016', marginBottom: 10 }}>You're all set!</h2>
        <p style={{ color: '#6B7F5A', lineHeight: 1.6 }}>Thank you for completing your onboarding! The Camp Phillip team will review your submission and be in touch soon. We can't wait to see you this summer! 🌲</p>
        <p style={{ marginTop: 12, fontSize: '0.85rem', color: '#6B7F5A' }}>Questions? Email <strong>Eli@campphillip.com</strong></p>
        <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {onGoToProfile && (
            <button onClick={onGoToProfile} style={{ padding: '12px 28px', background: '#F5A623', color: '#2D5016', border: 'none', borderRadius: 10, fontFamily: "'Nunito', sans-serif", fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' }}>
              👤 Go to My Profile
            </button>
          )}
          <button onClick={onEdit} style={{ padding: '11px 28px', background: 'transparent', color: '#2D5016', border: '2px solid #C8D9B0', borderRadius: 10, fontFamily: "'Nunito', sans-serif", fontSize: '0.88rem', fontWeight: 700, cursor: 'pointer' }}>
            ✏️ Edit My Submission
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  root: { fontFamily: "'Nunito', sans-serif", background: '#FDF8F0', minHeight: '100vh' },
  header: { background: 'linear-gradient(135deg, #2D5016 0%, #3E6B1F 60%, #7BA05B 100%)', color: '#fff', padding: '28px 24px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 },
  badge: { display: 'inline-block', background: '#F5A623', color: '#2D5016', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 2, textTransform: 'uppercase', padding: '4px 14px', borderRadius: 20, marginBottom: 8 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 900, margin: '0 0 4px' },
  sub: { fontSize: '0.9rem', opacity: 0.8, margin: 0 },
  signOutBtn: { padding: '7px 14px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontFamily: "'Nunito', sans-serif", fontSize: '0.85rem' },
  tabWrap: { background: '#2D5016', padding: '0 16px 12px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 12px rgba(0,0,0,0.15)' },
  tabRow: { display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none', paddingTop: 8 },
  tabBtn: { flex: '0 0 auto', padding: '8px 12px', border: 'none', background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.65)', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  tabActive: { background: '#F5A623', color: '#2D5016' },
  tabDone: { background: 'rgba(255,255,255,0.22)', color: 'rgba(255,255,255,0.9)' },
  main: { maxWidth: 760, margin: '0 auto', padding: '28px 16px 60px' },
  card: { background: 'rgba(255,255,255,0.97)', borderRadius: 16, padding: 28, boxShadow: '0 4px 32px rgba(0,0,0,0.18)', border: '1px solid #C8D9B0' },
  input: { width: '100%', padding: '10px 14px', border: '2px solid #C8D9B0', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.95rem', color: '#1C2B0E', background: '#fff', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '2px solid #C8D9B0', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.95rem', color: '#1C2B0E', background: '#fff', outline: 'none', resize: 'vertical', minHeight: 80, boxSizing: 'border-box' },
  shirtRow: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', background: '#F4F8EE', padding: '12px 14px', borderRadius: 10, border: '1px solid #C8D9B0' },
  shirtTotal: { marginTop: 12, background: '#2D5016', color: '#fff', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', fontWeight: 700 },
  qtyBtn: { width: 32, height: 32, border: '2px solid #7BA05B', background: '#fff', color: '#2D5016', borderRadius: 6, fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' },
  uploadField: { display: 'flex', alignItems: 'center', gap: 12, background: '#F4F8EE', border: '2px dashed #C8D9B0', borderRadius: 10, padding: '14px 16px', cursor: 'pointer', position: 'relative' },
  docCard: { display: 'flex', alignItems: 'center', gap: 14, background: '#F4F8EE', border: '1px solid #C8D9B0', borderRadius: 10, padding: '14px 16px', marginBottom: 10 },
  docBtn: { padding: '8px 16px', background: '#2E7DAF', color: '#fff', border: 'none', borderRadius: 7, fontFamily: "'Nunito', sans-serif", fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
  docLink: { color: '#2E7DAF', fontWeight: 700 },
  navRow: { display: 'flex', justifyContent: 'space-between', marginTop: 28, gap: 12 },
  backBtn: { padding: '12px 24px', background: '#F4F8EE', color: '#2D5016', border: '2px solid #C8D9B0', borderRadius: 10, fontFamily: "'Nunito', sans-serif", fontSize: '1rem', fontWeight: 700, cursor: 'pointer' },
  nextBtn: { padding: '12px 28px', background: '#2D5016', color: '#fff', border: 'none', borderRadius: 10, fontFamily: "'Nunito', sans-serif", fontSize: '1rem', fontWeight: 700, cursor: 'pointer' },
  submitBtn: { padding: '14px 36px', background: '#F5A623', color: '#2D5016', border: 'none', borderRadius: 10, fontFamily: "'Nunito', sans-serif", fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer' },
  ynBtn: { padding: '10px', border: '2px solid #C8D9B0', background: '#fff', borderRadius: 8, fontFamily: "'Nunito', sans-serif", fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer', color: '#1C2B0E' },
  ynYes: { background: '#27AE60', color: '#fff', borderColor: '#27AE60' },
  ynNo: { background: '#e74c3c', color: '#fff', borderColor: '#e74c3c' },
  ynPartial: { background: '#F5A623', color: '#2D5016', borderColor: '#F5A623' },
  chip: { padding: '7px 14px', border: '2px solid #C8D9B0', borderRadius: 20, fontSize: '0.84rem', fontWeight: 600, background: '#fff', cursor: 'pointer', color: '#1C2B0E', fontFamily: "'Nunito', sans-serif" },
  chipActive: { background: '#2D5016', color: '#fff', borderColor: '#2D5016' },
  radioOpt: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#F4F8EE', border: '2px solid #C8D9B0', borderRadius: 8, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600, marginBottom: 8 },
  checkLabel: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, background: '#F4F8EE', border: '2px solid #C8D9B0', borderRadius: 10, marginBottom: 12, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.5 },
};
